import { makeAutoObservable, runInAction } from 'mobx'
import {
  format,
  add,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  endOfDay,
} from 'date-fns'
import { Timestamp } from 'firebase/firestore'
import {
  getScheduleForDate,
  calculateAvailableTimeSlots,
  getDisabledDays,
} from '../../../shared/lib/schedulingUtils'

class BookingAddStore {
  step = 'Service' // 'Service', 'Client', 'Date'
  selectedServiceIds = []
  selectedClientId = ''
  selectedDate = null
  selectedTime = ''
  bookingIdForEdit = null
  staffId = null // To preserve staff on edit
  isLoading = false

  // Injected stores & services
  authStore
  clientsStore
  servicesStore
  bookingsStore
  router

  constructor(rootStore) {
    // `router` будет внедрен позже
    makeAutoObservable(
      this,
      {
        rootStore: false,
      },
      { autoBind: true },
    )
    // 2. Сохраняем ссылку на rootStore
    this.rootStore = rootStore

    // 3. Получаем все нужные сторы из rootStore
    this.authStore = rootStore.authStore
    this.clientsStore = rootStore.clientsStore
    this.servicesStore = rootStore.servicesStore
    this.bookingsStore = rootStore.bookingsStore
    this.organizationStore = rootStore.organizationStore
    this.staffStore = rootStore.staffStore
    this.errorStore = rootStore.errorStore
  }

  // --- Actions ---

  init() {
    // Загрузка данных (услуги, клиенты, бронирования) теперь происходит автоматически
    // в соответствующих сторах через reaction на смену организации.
    this.resetForm()
  }

  async loadBookingForEdit(bookingId) {
    console.log(
      `[BookingAddStore] loadBookingForEdit called for ID: ${bookingId}`,
    )
    this.resetForm()
    // Fetch booking directly to ensure data is available, even on page refresh
    const booking = await this.bookingsStore.getVisitById(bookingId)
    console.log(`[BookingAddStore] getVisitById returned:`, booking)
    if (booking) {
      runInAction(() => {
        this.bookingIdForEdit = booking.id
        this.selectedClientId = booking.clientId
        this.selectedServiceIds = [...booking.serviceIds]
        this.staffId = booking.staffId

        const startTime = booking.startTime?.toDate
          ? booking.startTime.toDate()
          : new Date(booking.startTime)
        this.setSelectedDate(startTime)
        this.setSelectedTime(format(startTime, 'HH:mm'))
      })
    } else {
      console.error(
        `[BookingAddStore] Booking with ID ${bookingId} NOT found. Redirecting...`,
      )
      this.router.push('/bookings')
    }
  }

  toggleService(serviceId) {
    const index = this.selectedServiceIds.indexOf(serviceId)
    if (index > -1) {
      this.selectedServiceIds.splice(index, 1)
    } else {
      this.selectedServiceIds.push(serviceId)
    }
  }

  resetSelectedServices() {
    this.selectedServiceIds = []
  }

  setStep(step) {
    this.step = step
  }

  setSelectedClientId(id) {
    this.selectedClientId = id
  }

  setSelectedDate(date) {
    this.selectedDate = date
    this.selectedTime = '' // Сбрасываем время при смене даты
  }

  setSelectedTime(time) {
    this.selectedTime = time
  }

  async createBooking() {
    if (!this.isFormValid) {
      this.errorStore.showError('Пожалуйста, заполните все поля')
      return
    }

    this.isLoading = true
    try {
      const client = this.clientsStore.clients.find(
        (c) => c.id === this.selectedClientId,
      )
      if (!client) throw new Error('Клиент не найден')

      const [hours, minutes] = this.selectedTime.split(':').map(Number)
      let startTime = setSeconds(
        setMinutes(setHours(this.selectedDate, hours), minutes),
        0,
      )
      let endTime = add(startTime, { minutes: this.totalServicesDuration })

      // TODO: Добавить выбор сотрудника в UI. Пока что берем первого.
      const staffId = this.staffStore.staff[0]?.id
      if (!staffId) {
        this.errorStore.showError('В организации нет сотрудников.')
        return
      }

      const bookingPayload = {
        clientId: this.selectedClientId,
        staffId: staffId,
        serviceIds: this.checkedServices.map((s) => s.id),
        startTime: Timestamp.fromDate(startTime),
        endTime: Timestamp.fromDate(endTime),
        totalPrice: this.totalPrice,
        comment: '', // TODO: Добавить поле для комментария в UI
      }

      // Исправлено: метод в сторе называется addVisit
      const success = await this.bookingsStore.addVisit(bookingPayload)

      if (!success) return

      runInAction(() => {
        this.errorStore.showSuccess('Бронирование успешно создано')
        this.resetForm()
        this.router.push('/bookings')
      })
    } catch (error) {
      console.error('Failed to create booking:', error)
      this.errorStore.showError('Ошибка при создании бронирования')
    } finally {
      runInAction(() => (this.isLoading = false))
    }
  }

  async updateBooking() {
    if (!this.isFormValid || !this.isEditMode) {
      this.errorStore.showError('Форма заполнена некорректно.')
      return
    }

    this.isLoading = true
    try {
      const [hours, minutes] = this.selectedTime.split(':').map(Number)
      const startTime = setSeconds(
        setMinutes(setHours(this.selectedDate, hours), minutes),
        0,
      )
      const endTime = add(startTime, { minutes: this.totalServicesDuration })

      const bookingPayload = {
        clientId: this.selectedClientId,
        staffId: this.staffId, // Preserve original staff member
        serviceIds: this.checkedServices.map((s) => s.id),
        startTime: Timestamp.fromDate(startTime),
        endTime: Timestamp.fromDate(endTime),
        totalPrice: this.totalPrice,
      }

      await this.bookingsStore.updateVisit(
        this.bookingIdForEdit,
        bookingPayload,
      )

      runInAction(() => {
        this.errorStore.showSuccess('Запись успешно обновлена')
        this.resetForm()
        this.router.push('/bookings')
      })
    } catch (error) {
      console.error('Failed to update booking:', error)
      this.errorStore.showError('Ошибка при обновлении бронирования')
    } finally {
      runInAction(() => (this.isLoading = false))
    }
  }

  resetForm() {
    this.step = 'Service'
    this.selectedClientId = ''
    this.selectedDate = null
    this.selectedTime = ''
    this.bookingIdForEdit = null
    this.staffId = null
    this.resetSelectedServices()
  }

  cancelAndGoBack() {
    this.resetForm()
    this.router.push('/bookings')
  }

  // --- Computed-свойства ---

  get checkedServices() {
    return this.servicesStore.services.filter((s) =>
      this.selectedServiceIds.includes(s.id),
    )
  }

  get totalPrice() {
    return this.checkedServices.reduce(
      (sum, service) => sum + Number(service.price || 0),
      0,
    )
  }

  get totalServicesDuration() {
    // Суммирует длительность услуг в минутах
    return this.checkedServices.reduce(
      (total, service) => total + (service.duration || 0),
      0,
    )
  }

  get totalServicesDurationString() {
    const totalMinutes = this.totalServicesDuration
    if (totalMinutes === 0) return '...'

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    let durationString = ''
    if (hours > 0) durationString += `${hours} ч `
    if (minutes > 0) durationString += `${minutes} мин`
    // Возвращаем '...' если ничего не выбрано, для красоты в UI
    return durationString.trim() || '...'
  }

  get existingBookings() {
    if (!this.selectedDate) return []

    const selectedDayStart = startOfDay(this.selectedDate)
    const selectedDayEnd = endOfDay(this.selectedDate)
    const bufferTime = this.organizationStore.organization.bufferTime || 0

    // Исправлено: свойство в сторе называется visits
    return this.bookingsStore.visits
      .filter((booking) => {
        const bookingStartTime = booking.startTime?.toDate
          ? booking.startTime.toDate()
          : null
        if (!bookingStartTime) return false

        return (
          booking.status !== 'canceled' &&
          bookingStartTime >= selectedDayStart &&
          bookingStartTime <= selectedDayEnd
        )
      })
      .map((booking) => ({
        start: booking.startTime.toDate(),
        // Добавляем буферное время к концу каждой существующей записи
        end: add(booking.endTime.toDate(), { minutes: bufferTime }),
      }))
  }

  get scheduleForSelectedDate() {
    if (!this.selectedDate) return null
    // TODO: Implement staff selection. For now, use the first staff member.
    const staffMember = this.staffStore.staff[0]
    return getScheduleForDate(this.selectedDate, staffMember)
  }

  get dayTimeline() {
    // `availableTimeSlots` уже возвращает массив доступных слотов в формате ['09:00', '09:15', ...]
    // и учитывает, выбраны ли услуги. Мы просто преобразуем его в формат, который ожидает UI.
    return this.availableTimeSlots.map((time) => ({
      time,
      // Все слоты в этом массиве по определению доступны.
      isAvailable: true,
    }))
  }

  get availableTimeSlots() {
    return calculateAvailableTimeSlots(
      this.selectedDate,
      this.totalServicesDuration,
      this.scheduleForSelectedDate,
      this.existingBookings,
      this.servicesStore.minServiceDuration,
    )
  }

  get disabledDaysForPicker() {
    // TODO: Implement staff selection. For now, use the first staff member.
    const staffMember = this.staffStore.staff[0]
    return getDisabledDays(staffMember)
  }

  get isFormValid() {
    return (
      this.checkedServices.length > 0 &&
      this.selectedClientId &&
      this.selectedDate &&
      this.selectedTime
    )
  }

  get isEditMode() {
    return !!this.bookingIdForEdit
  }
}

export default BookingAddStore
